import RoutesUtils from '../../js/RoutesUtils.js';

/**
 * El permiso de «Mis vacaciones», en un solo sitio: lo usan la ruta y todo el que enlace a ella.
 *
 * Existe por la primera de las dos reglas de pantalla que fijó dirección el 05/10/2026 (§9 del
 * `CLAUDE.md` del orquestador): **si una pantalla no es alcanzable, su punto de entrada no se
 * dibuja**, porque un botón que lleva a un 403 hace creer que le han quitado permisos. Y
 * «alcanzable» no se deduce del rol: manda el **menú efectivo** del usuario, que vive en Mongo
 * —`RoutesUtils.checkPermission` contesta 403 si la ruta no está en ese menú, aunque sea ADMIN—,
 * así que el botón VACACIONES del calendario se pinta si y solo si la ruta está en él.
 *
 * Con una sola pregunta y los mismos argumentos que la ruta, el enlace y la pantalla no pueden
 * contestar cosas distintas (el mismo motivo que `PermisosEmpleados`, revisión #1711).
 */

/**
 * Quién tiene la pantalla mientras el menú todavía no ha llegado. Con el menú cargado manda el
 * menú, que es el control de acceso real de las rutas.
 *
 * La pantalla es **para todo el personal**: cualquiera que trabaje aquí tiene vacaciones y tiene
 * que poder ver las suyas. El alta de la entrada de menú la da el lote
 * `fase-rh-mis-vacaciones-2026-10` para todos los roles menos CLIENTE, y el backend solo exige
 * estar identificado y devuelve siempre lo de quien pregunta. De ahí que esta lista sean los roles
 * de plantilla que usan la ERP a diario.
 */
const ROLES_PANTALLA = ['ADMIN', 'DIRECTOR', 'INSPECTOR', 'ADMINISTRATIVO', 'PRODUCCION',
    'COMERCIAL', 'PLANIFICADOR', 'ADMINISTRACION', 'TRAMITACION', 'ENTREGA', 'DTYCAL', 'ENAC',
    'INDUSTRIA', 'FVYFP'];

const PermisosVacaciones = {
    ROLES_PANTALLA: ROLES_PANTALLA,

    /**
     * Si esta persona puede abrir «Mis vacaciones». Es la pregunta de quien dibuja un punto de
     * entrada —hoy el botón VACACIONES del calendario— y la misma con la que resuelve la ruta.
     *
     * Lo que NO decide es si puede solicitar: eso lo contesta el servidor con sus impedimentos, y
     * la ventana los enseña junto al botón. Son dos cosas distintas y conviene no mezclarlas: a
     * quien le falta el saldo de partida la pantalla le sirve igual, porque es donde ve su saldo,
     * su historial y por qué no puede pedir todavía.
     */
    abrirMisVacaciones: function() {
        return RoutesUtils.tieneAcceso('/vacaciones/', ROLES_PANTALLA);
    },

    /**
     * Si esta persona puede **registrar vacaciones en nombre de otra** (#70): solo ADMIN y
     * DIRECTOR. Un aprobador corriente no, aunque apruebe las de esa misma persona: esto no es
     * aprobar lo que alguien pidió, es dar de alta días que nadie pidió y con fechas pasadas.
     *
     * Vive aquí y no en la pantalla por lo mismo que {@link abrirMisVacaciones}: lo preguntan la
     * ruta —que lo pasa como `prop`— y quien dibuje el botón, y con dos copias podrían contestar
     * cosas distintas. Es por rol y no por menú porque no abre ninguna ruta: es una ventana dentro
     * de una pantalla a la que ya se ha entrado, y el gate de verdad lo pone el servidor, que
     * contesta 403 a quien no sea ADMIN o DIRECTOR (I3).
     *
     * Y la respuesta decide si el botón **se dibuja**, no si se deshabilita (§9, regla 1 de
     * dirección del 05/10/2026): a quien no puede registrar no se le enseña un botón que va a dar
     * un 403, porque eso le hace creer que le han quitado permisos.
     */
    registrarEnNombreDe: function() {
        return RoutesUtils.checkRolesPermission(['ADMIN', 'DIRECTOR']);
    }
};

export default PermisosVacaciones;
